"use client"

import { useContext, useState, useEffect, useCallback } from "react"
import Logo from "../../images/vi-logo.png"
import Laptop from "../../images/laptop.png"
import RingTop from "../../images/ring1.png"
import RingBottom from "../../images/ring3.png"
import Mail from "../../images/mailicon.png"
import Lock from "../../images/lock-icon.png"
import { useForm } from "react-hook-form"
import { useLocation, useNavigate } from "react-router-dom"
import axios from "axios"
import { createDeviceAuthUrl, submitDeviceAuthUrl } from "../../Config/config"
import { UserContext } from "../../Contexts/UserContext"
import Constants from "../../Constants"

const DeviceAuthorization = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [loginErrorMsg, setLoginErrorMsg] = useState()
  const { setUser, setAccessData } = useContext(UserContext)
  const [authCode, setAuthCode] = useState("")
  const [countdown, setCountdown] = useState(120) // 2 minutes in seconds
  const [canResend, setCanResend] = useState(false)
  const redirectTo = useNavigate()
  const location = useLocation()

  // Countdown timer effect
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else {
      setCanResend(true)
    }
  }, [countdown])

  // Format countdown time as MM:SS
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }, [])

  //useForm
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      email: location.state?.email,
      "password": location.state.password,
      "auth_device_id": location.state.id,
      authCode: "",
    },
  })

  /**
   * The function `onSubmit` is used to submit device authentication data and handle the response.
   * @param data - An object containing the form data, including the email and authCode.
   * @returns The function `onSubmit` returns nothing.
   */
  const onSubmit = (data) => {
    if (isLoading) {
      return
    }
    setIsLoading(true)
    axios
      .post(submitDeviceAuthUrl, {
        email: data.email,
        password: location.state?.password,
        loginFrom: location.state?.loginFrom,
        device_auth_id: location.state?.deviceId,
        authCode: data.authCode,
      })
      .then((response) => {
        setIsLoading(false)
        if (response.status === 200) {
          if (response.data?.success === true) {
            const responseData = response.data?.data
            setAccessData({
              email: data.email,
              accessToken: responseData?.access_token,
              userId: responseData?.user_id,
              allowedModules: responseData?.allowed_modules,
            })
            localStorage.setItem(Constants.localStorageKeys.loggedInEmail, data?.email)
            localStorage.setItem(Constants.localStorageKeys.accessToken, response.data.data.access_token)
            localStorage.setItem(Constants.localStorageKeys.userId, response.data.data.user_id)
            localStorage.setItem(
              Constants.localStorageKeys.allowedModules,
              JSON.stringify(response.data.data.allowed_modules),
            )
            localStorage.setItem(Constants.localStorageKeys.studentLogin, response.data.data.studentLogin)
            localStorage.setItem(Constants.localStorageKeys.isExport, response.data.data.isExport)
            localStorage.setItem(Constants.localStorageKeys.isDirectoryUser, response.data.data.isDirectoryUser)
            localStorage.setItem(Constants.localStorageKeys.showPopup, response.data.data.popup_show_count)
            localStorage.setItem(Constants.localStorageKeys.exportOption, response.data.data.export_option)
            localStorage.setItem(Constants.localStorageKeys.exportExpireAt, response.data.data.export_expires_at)
            localStorage.setItem(
              Constants.localStorageKeys.accessControls,
              JSON.stringify(response.data.data.data_access_controls),
            )
            setUser((user) => ({
              name: data?.email,
              isAuth: true,
            }))
            // Get the intended destination from location state, fallback to server redirectUrl
            const intendedPath = location.state?.intendedPath || response.data?.data?.redirectUrl;
            redirectTo(intendedPath);
          } else {
            setLoginErrorMsg(response.data?.message)
          }
        }
      })
      .catch((error) => {
        setIsLoading(false)
      })
  }

  const createDeviceAuth = (data) => {
    setIsResending(true);
    setResendSuccess(false);
    axios.post(createDeviceAuthUrl, {
        "email": data.email,
        "password": data.password,
        "auth_device_id": data.auth_device_id
    }).then(response => {
        setIsResending(false);
        if (response.status === 200) {
          setResendSuccess(true);
          setTimeout(() => setResendSuccess(false), 5000);
        } else {
          setCanResend(true);
          setCountdown(0);
        }
    }).catch(error => {
        setIsResending(false);
        // On error, clear timer and enable resend button
        setCanResend(true);
        setCountdown(0);
    })
  }

  const handleResendCode = () => {
    if (!canResend || isResending) return;
    createDeviceAuth({
      email: location.state?.email,
      password: location.state?.password,
      auth_device_id: location.state?.deviceId,
      loginFrom: location.state.loginFrom,
    });
    // Reset countdown after resend
    setCountdown(120);
    setCanResend(false);
  }

  return (
    <>
      <style jsx>{`
        /* Centered container for right side */
        .right-side-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          padding: 2rem 1.5rem;
        }

        /* Logo styling */
        .centered-logo {
          width: 200px;
          margin-bottom: 3rem;
        }

        /* Compact card design that fits within the existing layout */
        .auth-card {
          width: 100%;
          max-width: 400px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(8px);
          border-radius: 12px;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .auth-header {
          text-align: center;
          padding: 2rem 2rem 0;
        }

        .auth-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 60px;
          height: 60px;
          background: #fef3c7;
          border-radius: 50%;
          color: #d97706;
          margin-bottom: 1rem;
        }

        .auth-card-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }

        .auth-content {
          padding: 1rem 2rem 2rem;
        }

        /* Compact Alert Styles */
        .alert {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 2rem;
        }

        .alert-error {
          background: #fef2f2;
          border: 1px solid #fecaca;
        }

        .alert-icon {
          color: #dc2626;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .alert-message {
          color: #b91c1c;
          font-weight: 500;
          margin: 0;
          line-height: 1.5;
          font-size: 0.9rem;
        }

        /* Input with integrated button */
        .input-container {
          position: relative;
          margin-bottom: 1rem;
          display: flex;
          justify-content: center;
        }

        .auth-input {
          width: 200px;
          margin: 0 auto;
          padding: 0.6rem 2rem 0.6rem 0.5rem;
          background: #FDF7E9;
          border: 2px solid #BC8B20;
          border-radius: 8px;
          font-size: 0.9rem;
          letter-spacing: 1px;
          text-align: center;
          outline: none;
          transition: all 0.2s ease;
        }

        .auth-input:focus {
          border-color: #A5711A;
          box-shadow: 0 0 0 3px rgba(188, 139, 32, 0.1);
        }

        .auth-input::placeholder {
          color: #9ca3af;
          letter-spacing: 2px;
        }

        .submit-button {
          position: absolute;
          right: calc(50% - 90px);
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          color: #A5711A;
          z-index: 2;
        }

        .submit-button:hover {
          color: #BC8B20;
          transform: translateY(-50%) scale(1.1);
        }

        .submit-button:disabled {
          color: #9ca3af;
          cursor: not-allowed;
          transform: translateY(-50%) scale(1);
        }

        /* Help Text */
        .help-section {
          text-align: center;
          margin-top: 1rem;
        }

        .help-subtext {
          font-size: 0.8rem;
          color: #9ca3af;
          margin-top: 1.5rem;
        }

        .resend-button {
          background: transparent;
          border: none;
          color: #BC8B20;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          padding: 0.5rem 1rem;
          // margin-top: 0.75rem;
          transition: all 0.2s ease;
          text-decoration: underline;
        }

        .resend-button:hover {
          color: #A5711A;
        }

        .resend-button:disabled {
          color: #9ca3af;
          cursor: not-allowed;
        }

        .resend-success {
          color: #BC8B20;
          font-size: 0.85rem;
          margin-top: 0.25rem;
        }

        /* Error message styling */
        .error-message {
          color: #dc2626;
          font-size: 0.875rem;
          margin-top: 0.5rem;
          text-align: center;
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .right-side-container {
            padding: 1.5rem;
          }
          
          .centered-logo {
            width: 180px;
            margin-bottom: 2rem;
          }
          
          .auth-card {
            max-width: 100%;
          }
        }

        @media (max-width: 640px) {
          .right-side-container {
            padding: 1rem;
          }
          
          .centered-logo {
            width: 160px;
            margin-bottom: 1.5rem;
          }
          
          .auth-header {
            padding: 1.5rem 1.5rem 0;
          }

          .auth-content {
            padding: 1rem 1.5rem 1.5rem;
          }

          .auth-card-title {
            font-size: 1.25rem;
          }
          
          .auth-icon {
            width: 50px;
            height: 50px;
          }
        }
      `}</style>

      <div>
        <div className="inset-0 h-full w-full object-cover">
          <div className="h-max">
            <div className="grid grid-cols-2">
              {/* <!-- left side--> */}
              <div className="lg:col-span-1 hidden lg:block h-screen lg:bg-[#DBB25B]">
                <div className="relative">
                  <img
                    src={RingTop || "/placeholder.svg"}
                    className="absolute w-[130px] ml-[62%] xl:ml-[65.25%] xl:mt-[10%] mt-[9%]"
                    alt=""
                  />
                  <img
                    src={RingBottom || "/placeholder.svg"}
                    className="absolute mt-[48%] ml-[12.5%] w-[60px]"
                    alt=""
                  />
                  <img src={Laptop || "/placeholder.svg"} className="absolute m-[16%]" alt="" />
                </div>
              </div>

              {/* <!--right side--> */}
              <div className="lg:col-span-1 col-span-2">
                <div className="right-side-container">
                  {/* Centered Logo */}
                  <img className="centered-logo" src={Logo || "/placeholder.svg"} alt="vi" />

                  {/* Centered Card */}
                  <div className="auth-card">
                    <div className="auth-header">
                      <div className="auth-icon">
                        <svg
                          width="28"
                          height="28"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <circle cx="12" cy="16" r="1" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      </div>
                      <h2 className="auth-card-title">Device Authorization</h2>
                    </div>

                    <div className="auth-content">
                      {/* Alert Message */}
                      <div className="alert alert-error">
                        <svg
                          className="alert-icon"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        <p className="alert-message">
                          The authorization code has been sent to your registered email address. Please enter the code
                          in the box below.
                        </p>
                      </div>

                      {/* Form with integrated input and button */}
                      <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="input-container">
                          <input
                            {...register("authCode", {
                              required: "Please enter your authorization code",
                              pattern: {
                                value: /^[A-Za-z0-9]{0,12}$/,
                                message: "Please enter a valid authorization code"
                              },
                              maxLength: {
                                value: 12,
                                message: "Maximum 12 characters allowed"
                              }
                            })}
                            type="text"
                            autoComplete="off"
                            placeholder=" "
                            onChange={(e) => {
                              // Only allow alphanumeric characters up to 12 characters
                              const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 12);
                              e.target.value = value;
                              setAuthCode(value);
                            }}
                            className="auth-input"
                          />
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="submit-button"
                          >
                            {isLoading ? (
                              <svg
                                className="animate-spin"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M21 12a9 9 0 11-6.219-8.56" />
                              </svg>
                            ) : (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M5 12h14" />
                                <path d="M12 5l7 7-7 7" />
                              </svg>
                            )}
                          </button>
                        </div>

                        {/* Error Messages */}
                        {errors.authCode && (
                          <p className="error-message">{errors.authCode?.message}</p>
                        )}
                        {loginErrorMsg && (
                          <p className="error-message">{loginErrorMsg}</p>
                        )}
                      </form>

                      {/* Help Text */}
                      <div className="help-section">
                        <button
                          type="button"
                          onClick={handleResendCode}
                          disabled={!canResend || isResending}
                          className="resend-button"
                        >
                          {isResending 
                            ? "Sending..." 
                            : canResend 
                              ? "Resend Code" 
                              : `Resend Code (${formatTime(countdown)}s)`
                          }
                        </button>
                        {resendSuccess && (
                          <p className="resend-success">Code sent successfully!</p>
                        )}
                        <p className="help-subtext">Check your spam/junk folder if you don't see the email</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default DeviceAuthorization